import { labelQuantity } from 'modules/utils/numbers/formatQuantity'

import { AntTag, ColumnsType } from '@agro-club/agroclub-shared'
import { DASH } from 'modules/constants'
import { CurrencySign, DeliveryBasis, Export, ExportStatus } from 'modules/domain/export/types'
import formatNumber from 'modules/utils/numbers/formatNumber'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { CellRenders } from 'views/components/Table/renderers'
import { TableTextLink } from 'views/ui/TableTextLink/TableTextLink'
import ExportRoutes from '../routes'
import { UserLink } from 'views/components/UserLink/UserLink'
import { Coordinator } from 'modules/domain/user/types'
import styled from 'styled-components'

const WrapWords = styled.div`
  word-break: break-word;
`

const useColumns = (): ColumnsType<Export> => {
  const { t } = useTranslation('export')

  return useMemo(
    () => [
      {
        title: t('crop'),
        dataIndex: 'product',
        width: 168,
        render: (product, row) => (
          <TableTextLink id="company" tableItemId={row.id} to={generatePath(ExportRoutes.Details, { id: row.id })}>
            {product.title}
          </TableTextLink>
        ),
      },
      {
        title: t('common:responsible'),
        dataIndex: 'responsible_coordinator',
        width: 168,
        render: (responsible: Coordinator) =>
          responsible && (
            <UserLink bodysize="bodyMedium" user={responsible}>
              <WrapWords>{responsible.full_name}</WrapWords>
            </UserLink>
          ),
      },
      {
        title: t('numberContract'),
        dataIndex: 'number',
        width: 130,
      },
      {
        title: t('status'),
        dataIndex: 'status',
        width: 108,
        align: 'center',
        render: (status) => {
          const isClosed = status === ExportStatus.closed
          const isActive = status === ExportStatus.active
          const text = (isClosed && t('closed')) || (isActive && t('active')) || t('fulfilled')
          const color = (isClosed && 'grey') || (isActive && 'green') || 'blue'
          return <AntTag color={color}>{text}</AntTag>
        },
      },
      {
        title: t('deliveryBasis'),
        dataIndex: 'delivery_basis',
        width: 130,
        render: (delivery_basis) => {
          const deliveryBasisMap = {
            [DeliveryBasis.CIF]: t('cif'),
            [DeliveryBasis.CFR]: t('cfrShort'),
            [DeliveryBasis.FOB]: t('fob'),
            [DeliveryBasis.DAP]: t('dap'),
            [DeliveryBasis.FCA]: t('fca'),
          }
          return deliveryBasisMap[delivery_basis]
        },
      },
      {
        title: t('consignmentDate'),
        width: 145,
        dataIndex: 'consignment_date',
        render: CellRenders.date,
      },
      {
        title: labelQuantity(t('exportVolume')),
        dataIndex: 'quantity',
        align: 'right',
        width: 145,
        render: (quantity) => formatNumber(quantity),
      },
      {
        title: t('contractValue'),
        dataIndex: 'price',
        align: 'right',
        width: 170,
        render: (price, row) => (price ? `${formatPrice(price)} ${CurrencySign[row.price_currency]}` : DASH),
      },
      {
        title: t('netRate'),
        dataIndex: 'net_rate',
        align: 'right',
        width: 116,
        render: (net_rate, rowData) => (net_rate ? `${net_rate} ${CurrencySign[rowData.hedge_currency]}` : undefined),
      },
      {
        title: t('settlementDate'),
        dataIndex: 'settlement_date',
        width: 128,
        render: CellRenders.date,
      },
      {
        title: t('buyer'),
        dataIndex: 'purchaser',
        width: 417,
        render: (purchaser) => purchaser?.name,
      },
      {
        title: t('vesselName'),
        dataIndex: 'ship',
        width: 417,
      },
    ],
    [t],
  )
}

export default useColumns
